import { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Badge, Table, Tabs, Tooltip, Whisper } from 'rsuite';
import { useAuth } from '../utils/useAuth';
import { getApiUrl, getAuthHeaders, getUserId } from '../utils/apiConfig';
import { formatDateTime } from '../utils/dateUtils';

const { Column, HeaderCell, Cell } = Table;

// Render an expiration duration (seconds) as days + hours, e.g. "2d 1h", "1h",
// "3d". Sub-hour remainders round to the nearest hour. No value -> "No expiration".
const formatExpiration = (seconds) => {
  const s = Number(seconds);
  if (!seconds || Number.isNaN(s) || s <= 0) return 'No expiration';
  let totalHours = Math.round(s / 3600);
  if (totalHours < 1) totalHours = 1;
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  if (days && hours) return `${days}d ${hours}h`;
  if (days) return `${days}d`;
  return `${hours}h`;
};

const getJobStatusBadge = (job) => {
  const status = job.status;
  if (status === 'completed') {
    return <Badge content="Completed" color="green" />;
  } else if (status === 'running') {
    const badge = <Badge content="Running" color="blue" />;
    if (job.stage) {
      return (
        <Whisper
          placement="topStart"
          trigger="hover"
          speaker={<Tooltip>{job.stage}</Tooltip>}
        >
          <span style={{ display: 'inline-block', cursor: 'default' }}>{badge}</span>
        </Whisper>
      );
    }
    return badge;
  } else if (status === 'error') {
    return <Badge content="Error" color="red" />;
  } else if (status === 'killed') {
    return <Badge content="Killed" color="orange" />;
  } else if (status === 'creating') {
    return <Badge content="Creating" color="cyan" />;
  } else {
    return <Badge content={status} color="gray" />;
  }
};

function JobDetailsDialog({ show, onClose, jobId }) {
  const auth = useAuth();
  const [jobDetails, setJobDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('table');

  const fetchJobDetails = useCallback(async () => {
    if (!jobId) return;
    
    setDetailsLoading(true);
    setJobDetails(null);
    
    const userId = getUserId(auth.user);
    if (!userId) {
      setJobDetails({ error: 'User ID not found. Please set up your user ID first.' });
      setDetailsLoading(false);
      return;
    }
    
    try {
      const headers = await getAuthHeaders(auth.getAccessTokenSilently);
      const response = await fetch(getApiUrl(`/users/${userId}/jobs/${jobId}`), {
        method: 'GET',
        headers: headers,
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch job details');
      }
      
      const data = await response.json();
      setJobDetails(data);
    } catch (error) {
      console.error('Error fetching job details:', error);
      setJobDetails({ error: error.message });
    } finally {
      setDetailsLoading(false);
    }
  }, [jobId, auth.user, auth.getAccessTokenSilently]);

  // Fetch job details when dialog opens or jobId changes
  useEffect(() => {
    if (show && jobId) {
      fetchJobDetails();
      setActiveTab('table'); // Reset to table tab when opening
    } else {
      // Reset state when dialog closes
      setJobDetails(null);
    }
  }, [show, jobId, fetchJobDetails]);

  return (
    <Modal 
      open={show} 
      onClose={onClose}
      size="lg"
    >
      <Modal.Header>
        <Modal.Title>Job Details: {jobId}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {detailsLoading ? (
          <div>Loading details...</div>
        ) : jobDetails?.error ? (
          <div style={{ color: 'red' }}>Error: {jobDetails.error}</div>
        ) : jobDetails ? (
          <Tabs activeKey={activeTab} onSelect={setActiveTab}>
            <Tabs.Tab eventKey="table" title="Table">
              <div style={{ marginTop: '15px' }}>
                <Table data={(() => {
                  const detailsArray = Object.keys(jobDetails)
                    .filter(key => {
                      // image_defaulted is folded into the `image` row's "(default)" marker.
                      if (key === 'error' || key === 'env' || key === 'provider_options' || key === 'image_defaulted') return false;
                      const value = jobDetails[key];
                      // Filter out null, undefined, and empty arrays
                      if (value === null || value === undefined) return false;
                      if (Array.isArray(value) && value.length === 0) return false;
                      if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) return false;
                      return true;
                    })
                    .map(key => ({
                      property: key,
                      value: jobDetails[key]
                    }));
                  
                  return detailsArray;
                })()} autoHeight wordWrap>
                  <Column width={200}>
                    <HeaderCell>Property</HeaderCell>
                    <Cell dataKey="property" />
                  </Column>
                  <Column flexGrow={1}>
                    <HeaderCell>Value</HeaderCell>
                    <Cell>
                      {(rowData) => {
                        const value = rowData.value;
                        if (rowData.property === 'description') {
                          return (
                            <span style={{
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              lineHeight: 1.4,
                            }}>
                              {value != null ? String(value) : '-'}
                            </span>
                          );
                        }
                        // Handle status badge
                        if (rowData.property === 'status') {
                          return getJobStatusBadge({ status: value, stage: jobDetails.stage });
                        }
                        // Image: mark "(default)" when it was filled from a default
                        // because the user did not supply one.
                        if (rowData.property === 'image') {
                          return (
                            <span>
                              <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{String(value)}</span>
                              {jobDetails.image_defaulted && (
                                <span style={{ color: '#aaa', fontStyle: 'italic', marginLeft: 6 }}>(default)</span>
                              )}
                            </span>
                          );
                        }
                        // Handle date fields
                        if (rowData.property === 'created_at' || rowData.property === 'completed_at' || rowData.property === 'last_used') {
                          return formatDateTime(value);
                        }
                        // Handle cost and price formatting
                        if (rowData.property === 'cost' || rowData.property === 'price_per_hour') {
                          return value !== null && value !== undefined ? `$${value.toFixed(2)}` : '-';
                        }
                        // Handle expiration: seconds -> human "2d 1h" / "1h"
                        if (rowData.property === 'expiration_seconds') {
                          return formatExpiration(value);
                        }
                        // Handle objects (like env)
                        if (typeof value === 'object' && !Array.isArray(value)) {
                          return (
                            <pre style={{ 
                              margin: 0, 
                              whiteSpace: 'pre-wrap',
                              background: rowData.property === 'error_message' ? '#ffe6e6' : '#2a2a2a',
                              color: rowData.property === 'error_message' ? 'red' : '#fff',
                              padding: '10px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              wordBreak: 'break-word'
                            }}>
                              {JSON.stringify(value, null, 2)}
                            </pre>
                          );
                        }
                        // Handle arrays
                        if (Array.isArray(value)) {
                          return value.join(', ');
                        }
                        return String(value);
                      }}
                    </Cell>
                  </Column>
                </Table>
              </div>
            </Tabs.Tab>
            {jobDetails.env && typeof jobDetails.env === 'object' && !Array.isArray(jobDetails.env) && Object.keys(jobDetails.env).length > 0 && (
              <Tabs.Tab eventKey="env" title="Env Vars">
                <div style={{ marginTop: '15px' }}>
                  <Table data={Object.keys(jobDetails.env).map(key => ({
                    key: key,
                    value: jobDetails.env[key]
                  }))} autoHeight>
                    <Column width={250}>
                      <HeaderCell>Variable Name</HeaderCell>
                      <Cell dataKey="key" />
                    </Column>
                    <Column flexGrow={1}>
                      <HeaderCell>Value</HeaderCell>
                      <Cell dataKey="value" />
                    </Column>
                  </Table>
                </div>
              </Tabs.Tab>
            )}
            <Tabs.Tab eventKey="provider_options" title="Provider Options">
              <div style={{ marginTop: '15px' }}>
                {jobDetails.provider_options && Object.keys(jobDetails.provider_options).length > 0 ? (
                  <pre style={{
                    background: '#2a2a2a',
                    color: '#fff',
                    padding: '15px',
                    borderRadius: '4px',
                    fontSize: '13px',
                    maxHeight: '400px',
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    margin: 0,
                  }}>
                    {JSON.stringify(jobDetails.provider_options, null, 2)}
                  </pre>
                ) : (
                  <div style={{ color: '#aaa', fontStyle: 'italic', padding: '10px 0' }}>
                    No provider options were specified for this job.
                    <br /><br />
                    <strong>Examples:</strong>
                    <pre style={{
                      background: '#2a2a2a',
                      color: '#9cdcfe',
                      padding: '12px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      marginTop: '10px',
                    }}>
{`# Vast.ai — pin to a specific host
{ "host_ids": [55838] }

# Vast.ai — pin to a specific physical machine
{ "machine_id": 12345 }

# Vast.ai — use known offer IDs (one chosen at random; bypasses marketplace search)
{ "offer_ids": [98765432] }

# Vast.ai — explore: prefer hosts outside the reliable-host registry
{ "host_ids": [] }

# DigitalOcean — assign to a specific VPC
{ "vpc_uuid": "c33931f2-a26a-4d9b-b53e-03fa4e51a836" }`}
                    </pre>
                  </div>
                )}
              </div>
            </Tabs.Tab>
            <Tabs.Tab eventKey="json" title="JSON">
              <div style={{ marginTop: '15px' }}>
                <pre style={{ 
                  background: '#2a2a2a', 
                  color: '#fff',
                  padding: '15px', 
                  borderRadius: '4px',
                  fontSize: '12px',
                  maxHeight: '500px',
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  margin: 0
                }}>
                  {JSON.stringify(jobDetails, null, 2)}
                </pre>
              </div>
            </Tabs.Tab>
          </Tabs>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onClose} appearance="primary">
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default JobDetailsDialog;

